"use client";

import {
	AboutTheProduct,
	type AboutProductContent,
	type UseAndCareGuide
} from "@/composites/product";
import type { BlockOf, CatalogSpecification } from "@/domain/catalog";
import { resolveAboutProductSectionOrder } from "../../helpers/resolve-about-product-order";

/**
 * PDP data the About the Product section shows alongside its own block:
 * the Materials bullet comes from `specification`, the "For Consultants"
 * accordion from the `consultant_view_content` block, and the guide
 * affordance from the `use_and_care_block`. `ProductDetailTemplate` resolves
 * all three (the consultant tips are gated on the user store) and threads
 * them through `ContentBlocksRenderer`.
 */
export interface AboutTheProductResolved {
	specification?: CatalogSpecification;
	consultantSellingTips?: string;
	useAndCareGuide?: UseAndCareGuide;
	useAndCareHref?: string;
	onUseAndCareClick?: () => void;
}

export interface AboutTheProductBlockProps {
	block: BlockOf<"about_product">;
	resolved?: AboutTheProductResolved;
}

/**
 * Adapter for the `about_product` modular block.
 *
 * Per `docs/BLOCK_COMPONENT.md`, this file is a thin shell over UI: it
 * unpacks the typed CMS payload — each group's RTE field plus its
 * `default_open` flag — and forwards display props. The composite decides
 * which accordions survive; a group whose RTE is empty renders nothing.
 */
export function AboutTheProductBlock({
	block,
	resolved
}: AboutTheProductBlockProps) {
	return (
		<AboutTheProduct
			specification={resolved?.specification}
			aboutProduct={toAboutProductContent(block)}
			sectionOrder={resolveAboutProductSectionOrder(block)}
			consultantSellingTips={resolved?.consultantSellingTips}
			useAndCareGuide={resolved?.useAndCareGuide}
			useAndCareHref={resolved?.useAndCareHref}
			onUseAndCareClick={resolved?.onUseAndCareClick}
		/>
	);
}

// `care_safety` names its RTE field `care_notes`; the other three use `content`.
function toAboutProductContent(
	block: BlockOf<"about_product">
): AboutProductContent {
	return {
		dimensions: {
			html: block.dimensions?.content,
			defaultOpen: block.dimensions?.default_open
		},
		features: {
			html: block.features?.content,
			defaultOpen: block.features?.default_open
		},
		careSafety: {
			html: block.care_safety?.care_notes,
			defaultOpen: block.care_safety?.default_open
		},
		replacementParts: {
			html: block.replacement_parts?.content,
			defaultOpen: block.replacement_parts?.default_open
		}
	};
}
